import { loader } from 'graphql.macro';
import styles from './DashboardV2.module.scss';
import { useMutation, useQuery } from '@apollo/client';
import { useCurrentUser } from '../../../providers/UserProvider';
import SectionContainer from './SectionContainer';
import { Text } from '../../../components/typography';
import { useTranslation } from 'react-i18next';
import { keys } from '../../../utilities/translator/translation_keys';
import SafetyShareCard from '../SafetyShareCard';
import {
  allStatuses,
  announcementIcons,
  getRoute,
  quickActionTypes,
  taskIntervalStringLookup,
} from '../../../constants/strings';
import DashboardCard from './DashboardCard';
import { getLocalTime } from '../../../utilities/time';
import DashboardNumberStats from './DashboardNumberStats';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { paths } from '../../../constants/strings';
import { useActionItemModal, useQueryParams } from '../../../hooks/misc';
import { useState, useRef, useEffect, useMemo, Suspense } from 'react';
import { useIsMobile } from '../../../hooks/misc';
import Dashboard from '../Dashboard';
import { useOnlineStatus } from '../../../hooks/offline-hooks/offline-misc';
import { useModal } from '../../../providers/ModalProvider';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { modals } from '../../../providers/modals';
import { openDailyReportModal, showToast } from '../../../graphql/cache/modal';
import QuickAction from '../QuickAction';
import { useWorkspace } from '../../../providers/WorkspaceProvider';
import NotificationList from '../../../components/notifications/NotificationList';
import {
  actionItemIcons,
  closeModalComponents,
  internalTypes,
} from '../../../utilities';
import moment from 'moment';
import party from '../../../assets/empty_state_images/party_actions.svg';
import CustomQuickAction from '../CustomQuickAction';
import { dashboardImages } from '../../../utilities/toolboxtalks';
import CustomSpinner from '../../../components/CustomSpinner';
import { useMyInspections } from '../../../providers/MyInspectionsProvider';
import { useOpenObservationModal } from '../../../hooks/useOpenObservationModal';
import SimpleTabSystem from '../../../components/SimpleTabSystem';
import { useMutationQueue } from '../../../providers/MutationQueueProvider';
import { useTemplates } from '../../../providers/TemplateProvider';
import OfflineBanner from '../../../components/navbar/OfflineBanner';
import OfflineDashboard from './OfflineDashboard';
import { useInspection } from '../../../hooks/offline-hooks/createInspectionhook';

const dashboardQuery = loader('./DashboardV2.graphql');
const announcementQuery = loader('./DashboardV2.announcements.graphql');
const snoozedAnnouncementQuery = loader(
  './DashboardV2.snoozedAnnouncements.graphql',
);
const taskQuery = loader('./DashboardV2.tasks.graphql');
const quickActionQuery = loader('./DashboardV2.quickAction.graphql');
const participatedInspectionsQuery = loader(
  './DashboardV2.participated.inspections.graphql',
);
const createMeetingInspectionMutation = loader(
  'src/components/modals/provider_modals/RecordMeetingModal.create.graphql',
);
const actionItemsQuery = loader('./Dashboard.actionItems.graphql');

const announcementLength = {
  min: 4,
  max: 200,
};

const views = ['WORKSPACE', 'ALL'];

export default function DashboardV2() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const {
    user,
    assessmentName,
    company,
    allTemplateTypes,
    defaultObservationWorkspaceId,
    syncing,
  } = useCurrentUser();
  const navigate = useNavigate();
  const { openActionItemModal } = useActionItemModal();
  const [showNotifications, setShowNotifications] = useState(false);
  const today = new moment().format('yyyy-MM-DD');
  const { openModal } = useModal();
  const { openObservationModal } = useOpenObservationModal();
  const { workspaceId, workspace, observationTemplate, allWorkspaces } =
    useWorkspace();
  const ref = useRef(null);
  const { enableDailyReports, enableTimecards, enableLoneWorker } = useFlags();
  const online = useOnlineStatus();
  const [isEditingButtons, setIsEditingButtons] = useState(false);
  const {
    myInspections = [],
    refetchMyInspections,
    loading: loadingMyInspections,
  } = useMyInspections();
  const [selectedAction, setSelectedAction] = useState('Assigned');
  const [selectedAssessment, setSelectedAssessment] = useState('Created');
  const [taskLookahead, setTaskLookahead] = useState('30 Days');
  const { queueIsFlushing } = useMutationQueue();
  const [createMeetingInspection] = useMutation(
    createMeetingInspectionMutation,
  );
  const { fetchTemplates } = useTemplates();
  const { getParam } = useQueryParams();
  const modalOpen = !!getParam('modalName');
  const { addInspection } = useInspection();

  const tabOptions = [
    { title: `${workspace?.title || 'WORKSPACE'}`, key: views[0] },
    { title: 'All Workspaces', key: views[1] },
  ];
  const allWorkspaceTitle = { title: t(keys.common.SHARED), id: null };

  const emptyState = (
    <div className={styles.emptyStateContainer}>
      <img src={party} alt="party" className={styles.emptyStateImage} />
      <Text noMargin size="sm" color="secondary" textAlign="center">
        {t(keys.dashboard.ACTION_ITEMS_EMPTY_STATE_TITLE)}
      </Text>
    </div>
  );

  const [announcementsExpanded, setAnnouncementsExpanded] = useState(false);
  const skip = !online || queueIsFlushing || syncing || modalOpen;

  const { data: { inspections: myParticipatedInspections = [] } = {} } =
    useQuery(participatedInspectionsQuery, {
      skip: selectedAssessment !== 'Participated' || skip,
      variables: {
        userId: `${user.id}`,
      },
    });
  const [selectedView, setSelectedView] = useState('ALL');

  const showInspections = useMemo(() => {
    const selected =
      selectedAssessment === 'Created'
        ? myInspections
        : myParticipatedInspections;
    if (selectedView === views[0] && !isMobile) {
      return selected.filter((i) => i.workspace?.id === workspaceId);
    }
    return selected;
  }, [
    isMobile,
    myInspections,
    myParticipatedInspections,
    selectedAssessment,
    selectedView,
    workspaceId,
  ]);

  const { filters: actionItemFilters, subfilters: actionItemSubfilters } =
    useMemo(() => {
      const filters = [
        { field: 'status', operator: 'not', value: ['DONE', 'DUPLICATE'] },
      ];
      const subfilters = [];

      switch (selectedAction) {
        case 'Participated':
          subfilters.push({
            association: 'participants',
            field: 'userId',
            operator: 'eq',
            value: [`${user?.id}`],
          });
          break;
        case 'Created':
          filters.push({
            field: 'creatorId',
            operator: 'eq',
            value: [`${user?.id}`],
          });
          break;
        default:
          filters.push({
            field: 'assigneeId',
            operator: 'eq',
            value: [`${user?.id}`],
          });
          break;
      }

      if (selectedView === views[0] && !isMobile) {
        filters.push({
          field: 'workspaceId',
          operator: 'eq',
          value: [`${workspaceId}`],
        });
      }

      return { filters, subfilters };
    }, [isMobile, selectedAction, selectedView, user?.id, workspaceId]);

  const {
    loading: loadingActionItems,
    data: { actionItems: showActionItems = [] } = {},
  } = useQuery(actionItemsQuery, {
    skip: skip,
    variables: {
      filters: actionItemFilters,
      subfilters: actionItemSubfilters,
    },
  });

  const {
    loading: loadingDashboardAssets,
    refetch,
    data: {
      actionItems = [],
      daysSinceLastIncident = 0,
      nearMissCount = 0,
      openActionItems = 0,
    } = {},
  } = useQuery(dashboardQuery, {
    skip: skip,
    variables: {
      userId: `${user.id}`,
      actionItemField: 'creatorId',
    },
    onCompleted: (data) => {
      if (
        online &&
        data?.inspections?.length > 0 &&
        myInspections?.length > 0 &&
        !queueIsFlushing &&
        !syncing
      ) {
        fetchTemplates();
        const latestDashboardInspectionId = data.inspections[0]?.id;
        const latestMyInspectionId = myInspections[0]?.id;
        if (latestDashboardInspectionId !== latestMyInspectionId) {
          refetchMyInspections();
        }
      }
    },
  });

  const {
    refetch: refetchQuickActions,
    data: {
      quickActions = [],
      workspaceQuickActions = [],
      companyQuickActions = [],
    } = {},
  } = useQuery(quickActionQuery, {
    skip: !user?.id || skip,
    variables: {
      userId: `${user.id}`,
    },
    onCompleted: (data) => {},
    fetchPolicy: 'network-only',
  });

  const now = moment();

  const taskLookaheadDays = parseInt(taskLookahead) || 30;
  const taskLookaheadDate = now
    .clone()
    .add(taskLookaheadDays, 'days')
    .format('YYYY-MM-DD');
  const {
    refetch: refetchTasks,
    data: {
      dailyTasks = [],
      weeklyTasks = [],
      monthlyTasks = [],
      quarterlyTasks = [],
      annuallyTasks = [],
      oneTimeTasks = [],
      customTasks = [],
    } = {},
  } = useQuery(taskQuery, {
    skip: !user?.id || !online || queueIsFlushing || syncing,
    variables: {
      userId: `${user.id}`,
      lookaheadDate: taskLookaheadDate,
      today: moment().format('YYYY-MM-DD'),
    },
    fetchPolicy: 'network-only',
  });

  const tasks = useMemo(() => {
    const allTasks = [
      ...oneTimeTasks,
      ...dailyTasks,
      ...weeklyTasks,
      ...monthlyTasks,
      ...quarterlyTasks,
      ...annuallyTasks,
      ...customTasks,
    ];
    if (selectedView === views[0]) {
      return allTasks?.filter((t) => t.workspace?.id === workspace?.id);
    }
    return allTasks;
  }, [
    dailyTasks,
    monthlyTasks,
    quarterlyTasks,
    annuallyTasks,
    oneTimeTasks,
    customTasks,
    selectedView,
    weeklyTasks,
    workspace?.id,
  ]);

  const customQuickActions = useMemo(() => {
    const qa = [
      ...quickActions,
      ...companyQuickActions,
      ...workspaceQuickActions,
    ];
    const currentWorkspaceQA = qa?.filter(
      (w) => w.workspace?.id === workspace?.id || !w.workspace?.id,
    );
    return selectedView === views[0] ? currentWorkspaceQA : qa;
  }, [
    companyQuickActions,
    quickActions,
    selectedView,
    workspace?.id,
    workspaceQuickActions,
  ]);

  const editableQuickAction = useMemo(() => {
    return [
      ...(isEditingButtons || !quickActions?.length
        ? [
            {
              icon: 'add',
              color: 'customButton',
              onClick: () =>
                openModal({
                  modalName: modals.quickAction,
                  variables: {
                    workspace: workspace,
                    quickActionType: quickActionTypes.userLevel,
                    refetch: refetchQuickActions,
                  },
                }),
            },
          ]
        : []),
      ...(quickActions?.length
        ? [
            {
              icon: isEditingButtons ? 'check' : 'edit',
              color: isEditingButtons ? 'editButton' : 'customButton',
              onClick: () => setIsEditingButtons(!isEditingButtons),
            },
          ]
        : []),
    ];
  }, [
    quickActions,
    isEditingButtons,
    openModal,
    refetchQuickActions,
    workspace,
  ]);

  const allBaseQuickActions = useMemo(() => {
    return [
      {
        title: user?.company?.assessmentName || t(keys.dashboard.ASSESS),
        icon: 'task',
        color: 'blue',
        key: 'ASSESSMENT',
        onClick: () => {
          openModal({
            modalName: modals.createAssessment,
            variables: {
              participants: [user],
              types: Object.values(allTemplateTypes).filter(
                (t) =>
                  t.key !== 'INCIDENT' &&
                  t.key !== 'OBSERVATION' &&
                  t.key !== 'AUDIT',
              ),
            },
          });
        },
      },
      ...(enableTimecards
        ? [
            {
              title: 'Log Time',
              icon: 'schedule',
              key: 'LOG_TIME',
              color: 'yellow',
              onClick: () => openModal({ modalName: modals.timecard }),
            },
          ]
        : []),
      {
        title: user.company?.observationName || 'Observation',
        icon: 'content_paste_search',
        color: 'darkBlue',
        key: 'OBSERVATION',
        onClick: () => {
          openObservationModal({
            targetWorkspaceId: defaultObservationWorkspaceId ?? workspaceId,
            fallbackTemplate: observationTemplate,
            variables: {
              participants: [],
              workspaceId: defaultObservationWorkspaceId ?? workspaceId,
              dateTime: new moment().format('YYYY-MM-DD HH:mm'),
              workspace: defaultObservationWorkspaceId
                ? allWorkspaces?.find(
                    (w) => w.id === defaultObservationWorkspaceId,
                  )
                : workspace,
            },
          });
        },
      },
      {
        title: t(keys.observations.RECOGNITION),
        icon: 'hotel_class',
        color: 'pink',
        key: 'RECOGNITION',
        onClick: () => {
          openModal({
            modalName: modals.observation,
            variables: {
              participants: [],
              workspaceId,
              templateId: observationTemplate?.id,
              dateTime: new moment().format('YYYY-MM-DD HH:mm'),
              type: 'RECOGNITION',
            },
          });
        },
      },
      {
        title: t(keys.common.MEETING),
        icon: 'mic',
        color: 'teal',
        key: 'MEETING',
        onClick: async () => {
          const {
            data: { useInternalTemplate },
          } = await createMeetingInspection({
            variables: {
              workspaceId,
              isDraft: true,
              reportedWorkspaceId: workspaceId,
              items: {},
              internalType: internalTypes.recordedMeeting,
              participants: [user.id],
            },
          });
          openModal({
            modalName: modals.recordMeeting,
            variables: {
              inspection: useInternalTemplate,
              participants: [user],
              refetch: () => refetch(),
            },
          });
        },
      },
      {
        title: t(keys.dashboard.ACTION),
        icon: 'task_alt',
        color: 'green',
        key: 'ACTION',
        onClick: () => openActionItemModal(null, 'GENERAL'),
      },
      {
        title: user.company?.incidentName || t(keys.common.INCIDENT),
        icon: 'fmd_bad',
        color: 'red',
        key: 'INCIDENT',
        onClick: () =>
          openModal({
            modalName: modals.createIncident,
            variables: { participants: [user] },
          }),
      },
      ...(enableDailyReports
        ? [
            {
              title: 'Daily Log',
              icon: 'inventory',
              color: 'purple',
              key: 'DAILY_LOG',
              onClick: () => {
                openDailyReportModal();
              },
            },
          ]
        : []),
      ...(enableLoneWorker
        ? [
            {
              title: user.company?.checkInName || 'Check In',
              icon: 'accessibility_new',
              key: 'CHECK_IN',
              color: 'orange',
              onClick: () =>
                openModal({
                  modalName: modals.checkIn,
                }),
            },
          ]
        : []),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enableDailyReports,
    enableTimecards,
    enableLoneWorker,
    isEditingButtons,
    quickActions?.length,
    user,
  ]);

  const baseQuickActions = useMemo(() => {
    const qa = company?.quickActions?.split('|') || [];
    return allBaseQuickActions?.filter((b) => qa?.includes(b.key));
  }, [allBaseQuickActions, company?.quickActions]);

  const {
    data: { announcementInteractions: dueAnnouncementInteractions = [] } = {},
  } = useQuery(announcementQuery, {
    skip: !online || queueIsFlushing,
    variables: {
      today,
      userId: `${user.id}`,
      pageSize: announcementLength.max,
    },
    fetchPolicy: 'network-only',
  });

  const {
    data: {
      announcementInteractions: snoozedAnnouncementInteractions = [],
    } = {},
  } = useQuery(snoozedAnnouncementQuery, {
    skip: !online || queueIsFlushing,
    variables: {
      userId: `${user.id}`,
      pageSize: announcementLength.max,
    },
    fetchPolicy: 'network-only',
  });

  const allAnnouncementInteractions = useMemo(() => {
    const byId = new Map();
    [
      ...dueAnnouncementInteractions,
      ...snoozedAnnouncementInteractions,
    ].forEach((interaction) => byId.set(interaction.id, interaction));
    return Array.from(byId.values()).sort(
      (a, b) => new Date(b.dateReleased) - new Date(a.dateReleased),
    );
  }, [dueAnnouncementInteractions, snoozedAnnouncementInteractions]);

  const announcementInteractions = useMemo(
    () =>
      allAnnouncementInteractions.slice(
        0,
        announcementsExpanded ? announcementLength.max : announcementLength.min,
      ),
    [allAnnouncementInteractions, announcementsExpanded],
  );

  const hideShowAll =
    allAnnouncementInteractions.length <= announcementLength.min;

  useEffect(
    () => closeModalComponents(ref, setShowNotifications),
    [setShowNotifications, ref],
  );

  const loading = loadingDashboardAssets || loadingMyInspections;

  const stats = [
    {
      title: t(keys.dashboard.DAYS_SINCE_LAST_INCIDENT),
      number: daysSinceLastIncident,
      image: dashboardImages.firstAidKit,
      card: 'card1',
    },
    {
      title: t(keys.dashboard.OPEN_ACTION_ITEMS),
      number: openActionItems,
      image: dashboardImages.clipboard,
      card: 'card2',
    },
    {
      title: t(keys.dashboard.NEAR_MISSES),
      number: nearMissCount,
      image: dashboardImages.writeReport,
      card: 'card3',
    },
  ];

  if (!online) {
    return (
      <Suspense fallback={null}>
        <OfflineDashboard
          inspections={myInspections}
          quickActions={[
            ...quickActions,
            ...companyQuickActions,
            ...workspaceQuickActions,
          ]}
        />
      </Suspense>
    );
  }
  if (loading) {
    return <></>;
  }

  if (!loading && online && !!queueIsFlushing && !!syncing) {
    return (
      <div className={styles.sync}>
        <OfflineBanner />
        <CustomSpinner
          text={'Syncing Data...'}
          className={styles.childSpinner}
        />
      </div>
    );
  }
  return isMobile ? (
    <Dashboard
      refetch={refetchQuickActions}
      loading={loading}
      tasks={tasks}
      actionItems={showActionItems}
      inspections={myInspections}
      quickActions={baseQuickActions}
      customQuickActions={customQuickActions}
      editableQuickAction={editableQuickAction}
      announcements={announcementInteractions}
      hideShowAll={hideShowAll}
      setAnnouncementsExpanded={setAnnouncementsExpanded}
      announcementExpanded={announcementsExpanded}
      isEditing={isEditingButtons}
    />
  ) : (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text size="lg" weight="bold" noMargin>
          {`${user.firstName ? `${user.firstName}'s` : ''} ${t(
            keys.dashboard.DASHBOARD,
          )}`}
        </Text>
        <SimpleTabSystem
          options={tabOptions}
          selected={selectedView || 'ALL'}
          setSelected={setSelectedView}
          navigation={false}
        />
      </div>

      <div className={styles.split}>
        <div className={styles.left}>
          <div className={styles.quickActions}>
            {baseQuickActions.map(
              (
                { title, color, icon, onClick, disabled, className, key },
                idx,
              ) => {
                return (
                  <QuickAction
                    key={title + idx + icon}
                    title={title}
                    icon={icon}
                    color={color}
                    onClick={onClick}
                    disabled={disabled}
                    className={className}
                  />
                );
              },
            )}
            {customQuickActions?.map((quickAction) => (
              <CustomQuickAction
                key={quickAction.id}
                quickAction={quickAction}
                refetch={refetchQuickActions}
                isEditing={
                  isEditingButtons &&
                  quickAction.type === quickActionTypes.userLevel
                }
              />
            ))}
            {editableQuickAction.map(
              ({ title, color, icon, onClick, disabled, className }, idx) => (
                <QuickAction
                  key={title + idx + icon}
                  title={title}
                  icon={icon}
                  color={color}
                  onClick={onClick}
                  disabled={disabled}
                  className={className}
                />
              ),
            )}
          </div>
          <br />
          <SafetyShareCard />
          <br />
          <div className={styles.contentContainer}>
            <SectionContainer
              title={
                !!assessmentName
                  ? `Recent ${assessmentName}`
                  : t(keys.dashboard.RECENT_ASSESSMENTS)
              }
              isLoading={loading}
              icon="task"
              options={['Created', 'Participated']}
              setSelected={setSelectedAssessment}
              selected={selectedAssessment}
              hasChildren={!!showInspections?.length}
              className={styles.splitSection}
              emptyState={emptyState}
              onClick={() => {
                const filter = new URLSearchParams({
                  creator: user.id,
                  allWorkspaces: 'TRUE',
                });
                navigate(getRoute(workspaceId, `assessments?${filter}`));
              }}
            >
              {!!showInspections?.length &&
                showInspections?.map(
                  ({
                    dateModified,
                    id,
                    isDraft,
                    subject,
                    workspaceId: inspectionWorkspaceId,
                    template: { title, templateType },
                    workspace,
                    status,
                  }) => {
                    return (
                      <DashboardCard
                        status={{
                          name:
                            t(allStatuses[status]?.title) ||
                            allStatuses[status]?.title,
                          color: allStatuses[status]?.color,
                        }}
                        key={id}
                        header={`${title} ${!!subject ? ` - ${subject}` : ''}`}
                        workspace={workspace || allWorkspaceTitle}
                        subtext={`${t(keys.dashboard.UPDATED)} ${getLocalTime(
                          dateModified,
                        ).fromNow()}`}
                        icon={
                          allTemplateTypes[templateType.toLowerCase()]?.icon ||
                          allTemplateTypes['fleet'].icon
                        }
                        iconColor={
                          allTemplateTypes[templateType.toLowerCase()]?.color ||
                          allTemplateTypes['fleet']?.color
                        }
                        onClick={() =>
                          navigate(
                            getRoute(
                              inspectionWorkspaceId || workspaceId,
                              paths.assessment,
                              id,
                            ),
                          )
                        }
                      />
                    );
                  },
                )}
            </SectionContainer>
            <SectionContainer
              title={t(keys.dashboard.ASSIGNED_ACTION_ITEMS, {
                variable: null,
              })}
              hasChildren={!!actionItems?.length}
              className={styles.splitSection}
              emptyState={emptyState}
              icon="task_alt"
              isLoading={loadingActionItems}
              options={['Assigned', 'Created', 'Participated']}
              setSelected={setSelectedAction}
              selected={selectedAction}
              onClick={() => {
                const filter = new URLSearchParams({
                  assignee: user.id,
                  allWorkspaces: 'TRUE',
                  tab: 'open',
                });
                navigate(getRoute(workspaceId, `actionitems?${filter}`));
              }}
            >
              {showActionItems?.map(
                ({
                  title,
                  priority,
                  dateCreated,
                  id,
                  dueDate,
                  creator,
                  workspace,
                  isCorrective,
                  type,
                  cpaId,
                  incident,
                  status,
                }) => (
                  <DashboardCard
                    key={id}
                    status={{
                      name:
                        t(allStatuses[status]?.title) ||
                        allStatuses[status]?.title,
                      color: allStatuses[status]?.color,
                    }}
                    header={title}
                    icon={
                      actionItemIcons(type?.toLowerCase(), allTemplateTypes)
                        ?.icon
                    }
                    workspace={workspace || allWorkspaceTitle}
                    iconColor={
                      actionItemIcons(type?.toLowerCase(), allTemplateTypes)
                        ?.color
                    }
                    rightContent={
                      <img
                        src={creator.avatarUrl}
                        className={styles.avatar}
                        alt="avatar"
                      />
                    }
                    subtext={
                      dueDate
                        ? `Due ${getLocalTime(dueDate).fromNow()}`
                        : `Created ${getLocalTime(dateCreated).fromNow()}`
                    }
                    onClick={() => {
                      if (isCorrective) {
                        navigate(`/${paths.cpa}/${cpaId}`);
                      } else if (type === 'INCIDENT_COMMENT') {
                        navigate(
                          `/${paths.incident}/${incident.id}?section=REVIEW`,
                        );
                      } else {
                        openActionItemModal(id, null, refetch);
                      }
                    }}
                  />
                ),
              )}
            </SectionContainer>
          </div>
        </div>
        <div className={styles.right}>
          <div className={styles.statsContainer}>
            <Box
              className={styles.statsBox}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gridTemplateRows: 'auto',
                gridTemplateAreas: `
                  "card1 card1"
                  "card2 card3"
                `,
              }}
            >
              {stats.map((stat) => {
                return (
                  <Box key={stat.title} sx={{ gridArea: stat.card }}>
                    <DashboardNumberStats
                      backgroundImage={stat.image}
                      number={stat.number}
                      text={stat.title}
                      maxWidth={stat.maxWidth}
                    />
                  </Box>
                );
              })}
            </Box>
          </div>
          <SectionContainer
            title={t(keys.tasks.MY_TASKS)}
            icon="assignment_turned_in"
            hasChildren={!!tasks?.length}
            emptyState={emptyState}
            options={['30 Days', '60 Days', '90 Days']}
            selected={taskLookahead}
            setSelected={setTaskLookahead}
          >
            {[
              'ONE_TIME',
              'DAILY',
              'WEEKLY',
              'MONTHLY',
              'QUARTERLY',
              'ANNUALLY',
              'CUSTOM',
            ].map((interval) => {
              const intervalTasks = tasks.filter(
                (t) => t.interval === interval && !!t.assigneeInfo?.length,
              );
              if (!intervalTasks.length) return null;

              return (
                <div key={interval}>
                  <Text
                    noMargin
                    size="xs"
                    textAlign="center"
                    weight="semiBold"
                    className={styles.intervalHeader}
                  >
                    {`${t(taskIntervalStringLookup[interval])}`}
                  </Text>
                  {intervalTasks.map((task) => {
                    const myInfo =
                      task.assigneeInfo?.find(
                        (a) => a.assignee?.id === user?.id,
                      ) ?? task.assigneeInfo?.[0];
                    const status = myInfo?.status;
                    const inspectionId = myInfo?.inspectionId;
                    const vehicle =
                      task.taskType === 'VEHICLE' ? myInfo?.vehicle : null;
                    const nextDue =
                      myInfo?.nextDueDate ?? task.scheduledStartDate;
                    return (
                      <DashboardCard
                        vehicle={vehicle}
                        status={{
                          name:
                            t(allStatuses[status]?.title) ||
                            allStatuses[status]?.title,
                          color: allStatuses[status]?.color,
                        }}
                        workspace={task.workspace || allWorkspaceTitle}
                        key={task?.id}
                        header={task?.title}
                        subtext={
                          interval === 'ONE_TIME'
                            ? !!task.dueDate
                              ? task.dueDate
                              : 'No Due Date'
                            : nextDue
                            ? `Due ${getLocalTime(nextDue).fromNow()}`
                            : 'No Due Date'
                        }
                        icon={
                          task.taskType === 'OBSERVATION'
                            ? 'content_paste_search'
                            : allTemplateTypes[
                                task?.template?.templateType?.toLowerCase()
                              ]?.icon || 'assignment_turned_in'
                        }
                        iconColor={
                          task.taskType === 'OBSERVATION'
                            ? 'red'
                            : allTemplateTypes[
                                task?.template?.templateType?.toLowerCase()
                              ]?.color || 'blue'
                        }
                        onClick={() => {
                          const vehicle = myInfo?.vehicle ?? null;
                          if (task.taskType === 'OBSERVATION') {
                            openObservationModal({
                              targetWorkspaceId:
                                defaultObservationWorkspaceId ?? workspaceId,
                              fallbackTemplate: observationTemplate,
                              variables: {
                                refetch: refetchTasks,
                                participants: [user],
                                workspaceId:
                                  defaultObservationWorkspaceId ?? workspaceId,
                                dateTime: new moment().format(
                                  'YYYY-MM-DD HH:mm',
                                ),
                              },
                            });
                          } else if (inspectionId) {
                            navigate(
                              getRoute(
                                task.workspace?.id || workspace?.id,
                                paths.assessment,
                                inspectionId,
                              ),
                            );
                          } else {
                            openModal({
                              modalName: modals.completeTask,
                              variables: {
                                task,
                                taskId: task.id,
                                template: task.template,
                                workspace: task.workspace,
                                taskType: task.taskType,
                                templateType: task.template?.templateType,
                                vehicle:
                                  (
                                    task.assigneeInfo?.find(
                                      (a) => a.assignee?.id === user?.id,
                                    ) ?? task.assigneeInfo?.[0]
                                  )?.vehicle ?? null,
                                participants: [user],
                                onStart: (participants) => {
                                  const { template } = task || {};
                                  const isPrivate =
                                    template?.templateType?.key ===
                                      'INCIDENT' || template?.private === true;
                                  addInspection({
                                    templateId: template?.id,
                                    vehicleId: vehicle?.id,
                                    inspectionType: template?.templateType,
                                    participants: participants?.map(
                                      (u) => u.id,
                                    ) ?? [user.id],
                                    workspaceId:
                                      allWorkspaces ||
                                      task.template?.templateType?.key ===
                                        'INCIDENT'
                                        ? null
                                        : task.workspace?.id,
                                    privateAssessment: isPrivate,
                                    subject: `${task.title}`,
                                  }).then((res) => {
                                    const id = res?.data?.addInspection?.id;
                                    const err = res?.errors;
                                    if (err?.length) {
                                      showToast({
                                        variant: 'warning',
                                        title: 'Error',
                                        message: `There was a problem starting the assessment. Error: ${err[0].message}`,
                                      });
                                    }
                                    if (!id) return;
                                    navigate(
                                      getRoute(
                                        task.workspace?.id || workspaceId,
                                        paths.assessment,
                                        id,
                                      ),
                                    );
                                  });
                                },
                              },
                            });
                          }
                        }}
                      />
                    );
                  })}
                </div>
              );
            })}
          </SectionContainer>
          <SectionContainer
            title={t(keys.dashboard.RECENT_ANNOUNCEMENTS)}
            hasChildren={!!announcementInteractions?.length > 0}
            emptyState={emptyState}
            isLoading={loading}
            icon="campaign"
            actionText={
              announcementsExpanded
                ? t(keys.dashboard.SHOW_LESS)
                : t(keys.dashboard.SHOW_MORE)
            }
            onClick={
              hideShowAll
                ? undefined
                : () => setAnnouncementsExpanded(!announcementsExpanded)
            }
          >
            <div>
              {announcementInteractions?.map(
                ({ dateReleased, status, announcement, id, signatureUrl }) => {
                  return (
                    <DashboardCard
                      key={id}
                      header={announcement.title}
                      subtext={dateReleased}
                      icon={announcementIcons[status].icon}
                      iconColor={announcementIcons[status].color}
                      onClick={() => {
                        openModal({
                          modalName: modals.announcement,
                          variables: {
                            announcement,
                            status,
                            id,
                            signatureUrl,
                          },
                        });
                      }}
                    />
                  );
                },
              )}
            </div>
          </SectionContainer>
        </div>
      </div>
      <NotificationList
        show={showNotifications}
        onHide={() => setShowNotifications(false)}
        expand={true}
        containerRef={ref}
      />
    </div>
  );
}
